/**
 * URL utilities for citation handling
 */

type WebContentItem =
  | string
  | {
      link?: string
      url?: string
      contents?: unknown
      content?: unknown
      snippet?: unknown
      title?: unknown
    }

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null
}

export const isWebContentItem = (value: unknown): value is WebContentItem => {
  return typeof value === "string" || isRecord(value)
}

/**
 * Normalize text fragment URLs to ensure # before :~:text=
 */
export const normalizeTextFragmentUrl = (url: string): string => {
  if (url.includes(":~:text=") && !url.includes("#:~:text=")) {
    return url.split(":~:text=").join("#:~:text=")
  }
  return url
}

/**
 * Normalize pathname by removing trailing slashes
 */
export const normalizePathname = (pathname: string): string => {
  if (!pathname) return "/"
  const trimmed = pathname.replace(/\/+$/, "")
  return trimmed || "/"
}

/**
 * Check if two URLs match (same origin, pathname, and search params)
 */
export const urlsMatch = (first: string | undefined, second: string): boolean => {
  if (!first || !second) return false
  if (first === second) {
    return true
  }

  // Early validation to avoid URL constructor errors
  if (!first.startsWith("http://") && !first.startsWith("https://")) {
    return false
  }
  if (!second.startsWith("http://") && !second.startsWith("https://")) {
    return false
  }

  try {
    const left = new URL(first)
    const right = new URL(second)
    return (
      left.origin === right.origin &&
      normalizePathname(left.pathname) === normalizePathname(right.pathname) &&
      left.search === right.search
    )
  } catch {
    // Silently fail for invalid URLs without flooding console
    return false
  }
}

/**
 * Extract web content chunk from various content item formats
 */
export const extractWebContentChunk = (
  item: WebContentItem,
  citation: string
): { text: string; title?: string } | null => {
  if (!item) {
    return null
  }

  const normalizedCitation = normalizeTextFragmentUrl(citation)
  const cleanCitation = normalizedCitation.split("#:~:text=")[0]

  if (typeof item === "string") {
    const trimmed = item.trim()
    if (!trimmed) {
      return null
    }

    if (trimmed.startsWith(`${cleanCitation}:`)) {
      return { text: trimmed.slice(cleanCitation.length + 1).trim() }
    }

    const [maybeUrl, rest] = trimmed.split(": ", 2)
    if (urlsMatch(maybeUrl, cleanCitation) && rest) {
      return { text: rest.trim() }
    }

    return null
  }

  if (!isRecord(item)) {
    return null
  }

  const urlCandidate = [item.url, item.link].find(
    (value): value is string => typeof value === "string" && value.length > 0
  )

  const textCandidate = [item.contents, item.content, item.snippet].find(
    (value): value is string => typeof value === "string" && value.trim().length > 0
  )

  if (!textCandidate || !urlsMatch(urlCandidate, cleanCitation)) {
    return null
  }

  return {
    text: textCandidate.trim(),
    title: typeof item.title === "string" ? item.title : undefined,
  }
}
